<script>
import { authComputed } from '@/state/helpers'
import moment from 'moment'
import api from '@/helpers/api'
import { sumBy } from 'lodash'

const CHART_COLORS = [
  '#556ee6',
  '#34c38f',
  '#f1b44c',
  '#f46a6a',
  '#50a5f1',
  '#74788d',
  '#e83e8c',
  '#6f42c1',
  '#20c997',
  '#fd7e14',
  '#5b73e8',
  '#343a40'
]

export default {
  name: 'quantity-donut',
  props: {
    startDate: {
      type: [String, Date],
      default: () => moment().startOf('month').format('YYYY-MM-DD')
    },
    endDate: {
      type: [String, Date],
      default: () => moment().endOf('month').format('YYYY-MM-DD')
    },
    tags: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: 'Title'
    },
    /** When set in non-production, skip API and render these quantities */
    mockSeries: {
      type: Array,
      default: null
    },
    height: {
      type: [Number, String],
      default: 320
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    compact: {
      type: Boolean,
      default: false
    },
    /** Show label/qty/share table beside the chart */
    sideLegend: {
      type: Boolean,
      default: false
    }
  },
  data: vm => {
    return {
      isLoaded: false,
      chartOptions: {},
      series: [],
      labels: []
    }
  },
  computed: {
    ...authComputed,
    total () {
      return sumBy(this.series || [], value => Number(value) || 0)
    },
    legendRows () {
      const labels =
        this.labels.length > 0
          ? this.labels
          : (this.chartOptions.labels || []).slice()
      return (this.series || []).map((quantity, index) => {
        const value = Number(quantity) || 0
        return {
          key: `${labels[index] || index}-${index}`,
          label: labels[index] || '—',
          quantity: value,
          share: this.total
            ? `${Math.round((value / this.total) * 100)}%`
            : '0%',
          color: CHART_COLORS[index % CHART_COLORS.length]
        }
      })
    },
    chartHeight () {
      if (this.sideLegend) {
        return typeof this.height === 'number'
          ? Math.min(this.height, 200)
          : 200
      }
      return this.height
    }
  },
  mounted () {
    this.chartOptions = {
      colors: CHART_COLORS,
      legend: {
        show: this.compact && !this.sideLegend,
        position: 'bottom',
        fontSize: '11px',
        itemMargin: {
          horizontal: 6,
          vertical: 2
        }
      },
      dataLabels: {
        enabled: !this.compact
      },
      plotOptions: {
        pie: {
          size: this.compact || this.sideLegend ? '70%' : '65%',
          donut: {
            size: this.compact || this.sideLegend ? '70%' : '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: this.compact || this.sideLegend ? '12px' : '22px',
                fontFamily: 'Rubik',
                color: '#74788d',
                offsetY: -10
              },
              total: {
                show: true,
                label: 'Total',
                fontSize: this.compact || this.sideLegend ? '12px' : '14px',
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b
                  }, 0)
                }
              },
              value: {
                show: true,
                color: undefined,
                offsetY: this.compact || this.sideLegend ? 4 : 16,
                fontSize: this.compact || this.sideLegend ? '16px' : '22px',
                formatter: function (val) {
                  return val
                }
              }
            }
          }
        }
      }
    }

    this.onRefresh()
  },
  watch: {
    startDate: 'onRefresh',
    endDate: 'onRefresh',
    tags: 'onRefresh',
    mockSeries: 'onRefresh',
    userProfile: 'onRefresh'
  },
  methods: {
    applySeries (labels, series) {
      this.labels = labels
      this.chartOptions = { ...this.chartOptions, labels }
      this.series = series
    },
    async onRefresh () {
      this.isLoaded = false
      const labels = this.tags.map(tag => tag.label).filter(Boolean)
      const hasMock =
        process.env.NODE_ENV !== 'production' &&
        Array.isArray(this.mockSeries) &&
        this.mockSeries.length

      const profile = this.userProfile || {}
      const { role, customer_account_id } = profile

      if (!role || (role === 'user' && customer_account_id === null)) {
        if (hasMock) {
          this.applySeries(labels, [...this.mockSeries])
        }
        this.isLoaded = true
        return
      }

      var fn = []
      var apiLabels = []

      this.tags.forEach(tag => {
        const { label, tags } = tag
        if (!tags) return
        fn = [
          ...fn,
          api.itemTagStatPerWeek.fetch({
            start_date: this.startDate,
            end_date: this.endDate,
            tags,
            customer_id: customer_account_id
          })
        ]
        apiLabels = [...apiLabels, label]
      })

      let results = []
      try {
        results = await Promise.all(fn)
      } catch (e) {
        results = fn.map(() => [])
      }
      const liveSeries = results.map(x => sumBy(x || [], 'quantity'))
      const liveTotal = sumBy(liveSeries)

      if (hasMock && liveTotal === 0) {
        this.applySeries(labels, [...this.mockSeries])
      } else {
        this.applySeries(apiLabels, liveSeries)
      }
      this.isLoaded = true
    }
  }
}
</script>
<template>
  <div class="quantity-donut" :class="{ 'quantity-donut--side': sideLegend }">
    <div
      v-if="showTitle"
      class="d-flex justify-content-between align-items-center mb-2"
    >
      <h5 class="m-0 mr-4">{{ title }}</h5>
      <div class="d-flex justify-content-end align-items-center">
        <b-btn
          variant="link"
          class="m-0"
          title="Refresh"
          v-on:click="onRefresh()"
          ><i class="bx bx-sync font-size-18"></i
        ></b-btn>
      </div>
    </div>

    <div class="quantity-donut__body">
      <div class="quantity-donut__chart">
        <apexchart
          type="donut"
          v-show="isLoaded"
          :height="chartHeight"
          :options="chartOptions"
          :series="series"
        ></apexchart>
      </div>

      <div v-if="sideLegend && isLoaded" class="quantity-donut__legend">
        <table v-if="legendRows.length">
          <thead>
            <tr>
              <th>{{ $t('dashboard.activity.col_type') }}</th>
              <th class="text-right">{{ $t('quantity') }}</th>
              <th class="text-right">{{ $t('dashboard.activity.col_share') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in legendRows" :key="row.key">
              <td>
                <span class="quantity-donut__swatch" :style="{ background: row.color }"></span>
                <span>{{ row.label }}</span>
              </td>
              <td class="text-right quantity-donut__qty">{{ row.quantity }}</td>
              <td class="text-right text-muted">{{ row.share }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{{ $t('Total') }}</td>
              <td class="text-right quantity-donut__qty">{{ total }}</td>
              <td class="text-right text-muted">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.quantity-donut--side {
  .quantity-donut__body {
    display: grid;
    grid-template-columns: minmax(110px, 0.85fr) minmax(0, 1.15fr);
    gap: 0.65rem;
    align-items: center;

    @media (max-width: 767.98px) {
      grid-template-columns: 1fr;
    }
  }
}

.quantity-donut__legend {
  min-width: 0;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.72rem;
  }

  th,
  td {
    padding: 0.22rem 0.15rem;
    border-bottom: 1px solid #eef2f7;
    vertical-align: middle;
  }

  th {
    color: #94a3b8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.62rem;
  }

  tfoot td {
    border-bottom: 0;
    padding-top: 0.35rem;
    font-weight: 500;
    color: #0f172a;
  }
}

.quantity-donut__swatch {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  margin-right: 0.3rem;
  vertical-align: middle;
  flex: 0 0 auto;
}

.quantity-donut__qty {
  color: #0f172a;
  font-weight: 500;
}
</style>
